import React, { useEffect, useRef, useState } from 'react';
import { TextField, IconButton, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { makeStyles } from '@mui/styles';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { debounce } from 'lodash';
import { CancelOutlined } from '@mui/icons-material';

interface TalentSearchBarProps {
    value: string;
    onSearch: (searchTerm: string) => void;
}

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: 1,
        maxWidth: '350px',
    },
    searchField: {
        flex: 1,
    },
}));

const TalentSearchBar: React.FC<TalentSearchBarProps> = ({ value, onSearch }) => {
    const classes = useStyles();
    const [input, setInput] = useState(value || '');
    const { themeStyle } = useThemeContext();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
        debouncedSearch(e.target.value);
    };

    const handleSearch = () => {
        onSearch(input.trim());
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const debouncedSearch = useRef(
        debounce((term: string) => {
            if (term.trim().length > 2) {
                onSearch(term);
            }

            if (term.length === 0) {
                onSearch('');
            }
        }, 500),
    ).current;

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <Box className={classes.root}>
            <TextField
                variant="outlined"
                placeholder="Search talent by name or services..."
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={classes.searchField}
                InputLabelProps={{
                    style: {
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    },
                }}
                inputProps={{
                    style: {
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    },
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: theme[themeStyle].tag_white, // ✅ change border color here
                        },
                        '&:hover fieldset': {
                            borderColor: theme[themeStyle].highlight, // optional: hover color
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: theme[themeStyle].highlight, // optional: focused color
                        },
                    },
                }}
                InputProps={{
                    endAdornment:
                        input.length > 0 ? (
                            <IconButton onClick={() => setInput('')} edge="end">
                                <CancelOutlined sx={{ color: theme[themeStyle].lightWhite }} />
                            </IconButton>
                        ) : (
                            <IconButton onClick={handleSearch} edge="end">
                                <SearchIcon sx={{ color: theme[themeStyle].lightWhite }} />
                            </IconButton>
                        ),
                }}
            />
        </Box>
    );
};

export default TalentSearchBar;
